import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { setOrgId, clearOrgId } from './services/orgStore';
import * as organisationService from './services/organisationService';
import Auth from './components/Auth';
import Dashboard from './components/layout/Dashboard';
import UpdatePassword from './components/UpdatePassword';

function App() {
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // From our 'users' table
  const [loading, setLoading] = useState(true);
  const [isRecovery, setIsRecovery] = useState(false);
  const [approvalError, setApprovalError] = useState('');

  // 1. AUTH & INITIAL DATA LOADING
  useEffect(() => {
    // Check for active session on load
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) checkUserApproval(session.user);
      else setLoading(false);
    });

    // Listen for changes (login, logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
      }

      setSession(session);
      if (session) {
        checkUserApproval(session.user);
      } else {
        clearOrgId();
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. CHECK IF USER IS APPROVED IN DATABASE
  const createUserProfile = async (authUser) => {
    // Attempt to extract Google ID if available, otherwise use a placeholder
    const googleId = authUser.identities?.[0]?.id || "unknown";
    
    const { error } = await supabase
      .from('users')
      .insert([
        { 
          user_id: authUser.id,
          email: authUser.email, 
          full_name: authUser.user_metadata.full_name || authUser.email,
          google_id: googleId,
          is_approved: false // Default to unapproved
        }
      ]);

    if (error) {
      console.error("Error creating user profile:", error);
      throw error;
    }
  };

  const checkUserApproval = async (authUser) => {
    try {
      setApprovalError('');

      const email = (authUser?.email || '').trim().toLowerCase();
      if (!email) throw new Error('Authenticated user has no email.');

      const { data: rows, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email);

      if (error) {
        // RLS / permission errors commonly surface here.
        throw error;
      }

      if (!rows || rows.length === 0) {
        console.log("Creating new user profile...");
        await createUserProfile({ ...authUser, email });
        // Re-fetch once. If RLS blocks inserts, createUserProfile will throw and we won't loop.
        const retry = await supabase.from('users').select('*').eq('email', email);
        if (retry.error) throw retry.error;
        if (!retry.data || retry.data.length === 0) throw new Error('User profile was not created.');
        const approved = retry.data.find((r) => r?.is_approved) || retry.data[0];
        setUserProfile(approved);
        return;
      }

      const approved = rows.find((r) => r?.is_approved) || rows[0];
      setUserProfile(approved);

      if (approved?.is_approved) {
        const { data: orgId, error: orgError } = await supabase
          .rpc('get_user_org_id');

        if (orgError) {
          console.error('Failed to fetch org_id:', orgError);
        } else if (orgId) {
          setOrgId(orgId);

          // Ensure new orgs default sales/purchase emails to user's email.
          // This runs only after approval + org selection and only fills missing values.
          try {
            const org = await organisationService.fetchOrganisation();
            const patch = {};
            if (!org?.sales_email) patch.sales_email = email;
            if (!org?.purchase_email) patch.purchase_email = email;
            if (Object.keys(patch).length > 0) {
              await organisationService.updateOrganisation(patch);
            }
          } catch (e) {
            // Don't block login if org settings can't be updated (RLS, etc.)
            console.warn('Unable to default org emails:', e);
          }
        } else {
          console.warn('User has no organization membership.');
        }
      }

    } catch (err) {
      console.error(err);
      const msg =
        err?.code === '42501'
          ? 'Blocked by Supabase Row Level Security (RLS) on table "users". You are signed in, but this client cannot read/insert your profile row.'
          : (err?.message || 'Failed to check approval status.');
      setApprovalError(msg);
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    clearOrgId();
    await supabase.auth.signOut();
  };

  // --- VIEWS ---

  if (loading) return <div style={{ padding: "20px" }}>Loading...</div>;

  if (isRecovery) {
    return (
      <UpdatePassword 
        onUpdated={async () => {
          await handleLogout(); // Sign out to force re-login with new password
          setIsRecovery(false);
        }}
      />
    );
  }

  // View 1: Not Logged In
  if (!session) {
    return <Auth session={session} />;
  }

  // View 2: Logged In but Not Approved
  if (!userProfile || !userProfile.is_approved) {
    if (approvalError) {
      return (
        <div style={{ padding: "50px", textAlign: "center", fontFamily: "sans-serif" }}>
          <h1>Login configuration error</h1>
          <p style={{ maxWidth: 720, margin: "12px auto", color: "#444" }}>{approvalError}</p>
          <p style={{ maxWidth: 720, margin: "12px auto", color: "#666", fontSize: "0.95em" }}>
            Fix: add a Supabase RLS policy that allows authenticated users to select/insert their own row in <code>users</code>,
            or move this approval check to a server/edge function using the service role key.
          </p>
          <button
            onClick={handleLogout}
            style={{ padding: "10px", marginTop: "20px", cursor: "pointer" }}
          >
            Sign Out
          </button>
        </div>
      );
    }
    return (
      <div style={{ padding: "50px", textAlign: "center", fontFamily: "sans-serif" }}>
        <h1>Access Pending</h1>
        <p>Your account is waiting for administrator approval.</p>
        <p>Please contact support or check back later.</p>
        <button 
          onClick={handleLogout}
          style={{ padding: "10px", marginTop: "20px", cursor: "pointer" }}
        >
          Sign Out
        </button>
      </div>
    );
  }

  // View 3: Main App (Approved)
  return (
    <Dashboard session={session} onLogout={handleLogout} />
  );
}

export default App;


